import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, Users, ShoppingCart, Package, BarChart3, Activity,
  DollarSign, Database as DatabaseIcon, Upload, Crown, UserX
} from 'lucide-react';
import api from '../api';
import { calcPricing } from '../hooks/usePricing';
import { LeadCharts } from './LeadCharts';
import Modal from './Modal';
import AdminOrdersTable from './AdminOrdersTable';

const PERIOD_OPTIONS = [
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
  { label: '90 dias', days: 90 },
];

function getAvatarColor(email) {
  if (!email) return 'var(--text-muted)';
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash % 360);
  return `linear-gradient(135deg, hsl(${h}, 65%, 55%) 0%, hsl(${(h + 40) % 360}, 75%, 40%) 100%)`;
}

function getInitials(email) {
  if (!email) return '??';
  const namePart = email.split('@')[0];
  const parts = namePart.split(/[._-]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return namePart.slice(0, 2).toUpperCase();
}

export default function AdminDashboardOverview({ results, excludedResults = [], status }) {
  const [orders, setOrders] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState('segmento');
  const [salesPeriod, setSalesPeriod] = useState(30);
  const [excludedOpen, setExcludedOpen] = useState(false);

  const fetchOrders = async () => {
    try {
      const ordersRes = await api.get('/orders');
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
    } catch { /* silently skip */ }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [ordersRes, statsRes] = await Promise.all([
          api.get('/orders'),
          api.get('/admin/stats'),
        ]);
        if (!cancelled) {
          setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
          if (statsRes.data) setAdminStats(statsRes.data);
        }
      } catch { /* silently skip */ }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const refreshOrders = () => fetchOrders();

  // ── Derived Metrics ──
  const totalLeads = results.length;
  const emails = results.filter((l) => l.Emails && l.Emails !== 'N/A').length;
  const whats = results.filter((l) => l.WhatsApp && l.WhatsApp !== 'N/A').length;
  const ambos = results.filter(
    (l) => l.Emails && l.Emails !== 'N/A' && l.WhatsApp && l.WhatsApp !== 'N/A'
  ).length;

  const categorias = Array.from(new Set(results.map((r) => r.Categoria).filter(Boolean)));
  const estados = Array.from(new Set(results.map((r) => r.Estado).filter(Boolean)));

  const paidOrders = orders.filter((o) => ['paid', 'approved', 'simulated'].includes(o.status));
  const pendingOrders = orders.filter((o) => o.status === 'pending' || o.status === 'requested_export' || o.status === 'created');

  const totalLeadsVendidos = paidOrders.reduce((sum, o) => {
    const notesMatch = (o.notes || '').match(/items=(\d+)/);
    return sum + (notesMatch ? parseInt(notesMatch[1]) : (o.packs || 1) * 25);
  }, 0);

  const totalRevenue = paidOrders.reduce((sum, o) => {
    const packs = o.packs || 1;
    const tier = o.tier || 'standard';
    const c = calcPricing(tier, packs);
    return sum + c.total;
  }, 0);

  const uniqueClients = new Set(orders.map((o) => o.owner || 'anon').filter(e => e !== 'anon')).size;

  // Sales timeline filtered by period
  const salesTimeline = useMemo(() => {
    if (!adminStats?.sales_timeline) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - salesPeriod);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return adminStats.sales_timeline.filter(d => d.date >= cutoffStr);
  }, [adminStats, salesPeriod]);

  const kpis = [
    { icon: DatabaseIcon, label: 'Leads na Base', value: totalLeads.toLocaleString('pt-BR'), color: 'var(--accent)', type: 'leads', onClick: () => { setAnalyticsTab('segmento'); setAnalyticsOpen(true); } },
    { icon: ShoppingCart, label: 'Leads Vendidos', value: totalLeadsVendidos.toLocaleString('pt-BR'), color: 'var(--accent-strong)', type: 'sales', onClick: () => { setAnalyticsTab('vendas'); setAnalyticsOpen(true); } },
    { icon: DollarSign, label: 'Receita Total', value: `R$ ${totalRevenue.toFixed(2)}`, color: '#10b981', type: 'revenue' },
    { icon: Users, label: 'Clientes Ativos', value: uniqueClients, color: '#ff7a59', type: 'clients' },
    { icon: Package, label: 'Pedidos Pagos', value: paidOrders.length, color: 'var(--accent-strong)', type: 'sales' },
    { icon: Activity, label: 'Pedidos Pendentes', value: pendingOrders.length, color: '#ffd24f', type: 'clients' },
    { icon: UserX, label: 'Leads Excluídos', value: excludedResults.length, color: '#ff5f70', type: 'danger', onClick: () => setExcludedOpen(true) },
  ];

  const ANALYTICS_TABS = [
    { key: 'segmento', label: 'Segmento' },
    { key: 'estado', label: 'Estado' },
    { key: 'contatos', label: 'Contatos' },
    { key: 'vendas', label: 'Vendas' },
  ];

  const statusClass = status?.is_running ? (status?.is_paused ? 'pulsing-dot--paused' : 'pulsing-dot--active') : 'pulsing-dot--stopped';
  const statusLabel = status?.is_running ? (status?.is_paused ? 'Pausado' : 'Rodando') : 'Parado';

  return (
    <>
      <div className="admin-overview">
        {/* ── Page Title ── */}
        <div className="admin-overview__header" style={{ marginBottom: '2rem' }}>
          <h1 className="admin-overview__title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BarChart3 size={28} style={{ color: 'var(--accent)' }} />
            Dashboard <span style={{ color: 'var(--accent)' }}>Admin</span>
          </h1>
          <p className="admin-overview__subtitle" style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Visão geral da operação, métricas de vendas e saúde da base de dados.
          </p>
        </div>

        {/* ── Admin Orders Control ── */}
        <AdminOrdersTable orders={orders} onStatusChange={refreshOrders} />

        {/* ── KPI Grid ── */}
        <div className="admin-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2.5rem' }}>
          {kpis.map((kpi) => (
            <div
              key={kpi.label}
              className={`admin-kpi-card-new admin-kpi-card-new--${kpi.type} ${kpi.onClick ? 'admin-kpi-card--clickable' : ''}`}
              onClick={kpi.onClick}
              style={{ cursor: kpi.onClick ? 'pointer' : 'default' }}
            >
              <div className="admin-kpi-card-new__icon-container" style={{ color: kpi.color }}>
                <kpi.icon size={22} />
              </div>
              <div className="admin-kpi-card-new__content">
                <span className="admin-kpi-card-new__label">{kpi.label}</span>
                <strong className="admin-kpi-card-new__value" style={{ color: kpi.color }}>{kpi.value}</strong>
              </div>
            </div>
          ))}
        </div>

        {/* ── Dashboard Charts ── */}
        <div className="admin-charts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
          <div className="admin-executive-card" style={{ paddingBottom: '1.5rem' }}>
            <h3 className="admin-bi-section-title">Segmentação por Categoria</h3>
            <LeadCharts leads={results} only="segmentacao" />
          </div>
          <div className="admin-executive-card" style={{ paddingBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 className="admin-bi-section-title" style={{ width: '100%' }}>Cobertura por Região</h3>
            <LeadCharts leads={results} only="regiao" />
          </div>
        </div>

        {/* ── Detail Panels ── */}
        <div className="admin-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
          {/* Contatos */}
          <div className="admin-executive-card" onClick={() => { setAnalyticsTab('contatos'); setAnalyticsOpen(true); }} style={{ cursor: 'pointer' }}>
            <h3 className="admin-bi-section-title">Contatos</h3>
            <div className="admin-detail-card__stats">
              <div className="admin-detail-stat">
                <span style={{ fontSize: '1.8rem', fontWeight: '700', color: 'var(--text-primary)' }}>{emails}</span>
                <small>E-mail</small>
              </div>
              <div className="admin-detail-stat">
                <span style={{ fontSize: '1.8rem', fontWeight: '700', color: 'var(--accent)' }}>{whats}</span>
                <small>WhatsApp</small>
              </div>
              <div className="admin-detail-stat">
                <span style={{ fontSize: '1.8rem', fontWeight: '700', color: '#10b981' }}>{ambos}</span>
                <small>Ambos</small>
              </div>
            </div>
          </div>

          {/* Cobertura */}
          <div className="admin-executive-card" onClick={() => { setAnalyticsTab('estado'); setAnalyticsOpen(true); }} style={{ cursor: 'pointer' }}>
            <h3 className="admin-bi-section-title">Cobertura</h3>
            <div className="admin-detail-card__stats">
              <div className="admin-detail-stat">
                <span style={{ fontSize: '1.8rem', fontWeight: '700', color: 'var(--accent)' }}>{estados.length}</span>
                <small>Estados</small>
              </div>
              <div className="admin-detail-stat">
                <span style={{ fontSize: '1.8rem', fontWeight: '700', color: '#ff7a59' }}>{categorias.length}</span>
                <small>Segmentos</small>
              </div>
            </div>
          </div>

          {/* Status Extração */}
          <div className="admin-executive-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <h3 className="admin-bi-section-title">Motor de Extração</h3>
            <div className="admin-detail-card__status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
              <div className="pulsing-dot-container" style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                <div className={`pulsing-dot ${statusClass}`} />
                <span>{statusLabel}</span>
              </div>
              {status?.results_count !== undefined && (
                <span className="admin-detail-card__meta" style={{ fontSize: '0.82rem', padding: '4px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  Cache: {status.results_count} leads
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Rankings Side by Side ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '2.5rem' }}>
          {/* Top Buyers */}
          {adminStats?.top_buyers?.length > 0 ? (
            <div className="admin-executive-card">
              <h3 className="admin-bi-section-title"><Crown size={18} style={{ color: '#fbbf24' }} /> Maiores Compradores</h3>
              <div className="admin-ranking-list-new">
                {adminStats.top_buyers.slice(0, 5).map((buyer, i) => {
                  const initials = getInitials(buyer.email);
                  const avatarBg = getAvatarColor(buyer.email);
                  return (
                    <div key={buyer.email} className="admin-ranking-item-new">
                      <div className="admin-ranking-item-new__position">
                        <span className={i === 0 ? 'ranking-medal-gold' : i === 1 ? 'ranking-medal-silver' : i === 2 ? 'ranking-medal-bronze' : ''}>
                          #{i + 1}
                        </span>
                      </div>
                      <div className="admin-ranking-item-new__avatar" style={{ background: avatarBg }}>
                        {initials}
                      </div>
                      <div className="admin-ranking-item-new__info">
                        <span className="admin-ranking-item-new__title">{buyer.email}</span>
                        <span className="admin-ranking-item-new__subtitle">{buyer.total_orders} pedidos</span>
                      </div>
                      <div className="admin-ranking-item-new__value">
                        <strong>{buyer.total_leads}</strong>
                        <small>leads</small>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="admin-executive-card">
              <h3 className="admin-bi-section-title"><Crown size={18} style={{ color: '#fbbf24' }} /> Maiores Compradores</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>Sem dados de compradores disponíveis.</p>
            </div>
          )}

          {/* Inactive Clients */}
          {adminStats?.inactive_clients?.length > 0 ? (
            <div className="admin-executive-card">
              <h3 className="admin-bi-section-title"><UserX size={18} style={{ color: '#ff5f70' }} /> Clientes Inativos <small style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>(sem compras há 30+ dias)</small></h3>
              <div className="admin-ranking-list-new">
                {adminStats.inactive_clients.slice(0, 5).map((client) => {
                  const initials = getInitials(client.email);
                  const avatarBg = getAvatarColor(client.email);
                  return (
                    <div key={client.email} className="admin-ranking-item-new">
                      <div className="admin-ranking-item-new__avatar" style={{ background: avatarBg }}>
                        {initials}
                      </div>
                      <div className="admin-ranking-item-new__info">
                        <span className="admin-ranking-item-new__title">{client.email}</span>
                        <span className="admin-ranking-item-new__subtitle">
                          Último pedido: {client.last_order_at ? new Date(client.last_order_at).toLocaleDateString('pt-BR') : '—'}
                        </span>
                      </div>
                      <span className="ui-badge ui-badge--neutral" style={{ fontSize: '0.65rem' }}>Inativo</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="admin-executive-card">
              <h3 className="admin-bi-section-title"><UserX size={18} style={{ color: '#ff5f70' }} /> Clientes Inativos</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>Nenhum cliente inativo no momento.</p>
            </div>
          )}
        </div>

        {/* ── Recent Orders ── */}
        {orders.length > 0 && (
          <div className="admin-executive-card" style={{ marginBottom: '2rem' }}>
            <h3 className="admin-bi-section-title">Pedidos Recentes</h3>
            <div className="admin-orders-table-wrap" style={{ overflowX: 'auto' }}>
              <table className="results-table admin-orders-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>ID</th>
                    <th style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Status</th>
                    <th style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Tier</th>
                    <th style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Packs</th>
                    <th style={{ background: 'transparent', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.slice(0, 10).map((o) => (
                    <tr key={o.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--accent)', padding: '0.85rem 1rem' }}>
                        {o.id?.slice(0, 8)}
                      </td>
                      <td style={{ padding: '0.85rem 1rem' }}>
                        <span className={`ui-badge ${o.status === 'paid' || o.status === 'approved' ? 'ui-badge--green' : o.status === 'pending' || o.status === 'requested_export' ? 'ui-badge--cyan' : 'ui-badge--neutral'}`} style={{ fontSize: '0.65rem' }}>
                          {o.status}
                        </span>
                      </td>
                      <td style={{ textTransform: 'capitalize', padding: '0.85rem 1rem' }}>{o.tier || 'standard'}</td>
                      <td style={{ padding: '0.85rem 1rem', fontWeight: '600' }}>{o.packs || 1}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '0.85rem 1rem' }}>
                        {o.created_at ? new Date(o.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Analytics Modal (Unified with Tabs) ── */}
      <Modal open={analyticsOpen} onClose={() => setAnalyticsOpen(false)}>
        <div className="analytics-modal">
          <h3 className="analytics-modal__title">
            <BarChart3 size={18} /> Analytics da Base
          </h3>

          {/* Tabs */}
          <div className="analytics-tabs">
            {ANALYTICS_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`analytics-tab ${analyticsTab === tab.key ? 'analytics-tab--active' : ''}`}
                onClick={() => setAnalyticsTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="analytics-content">
            {analyticsTab === 'segmento' && <LeadCharts leads={results} only="segmentacao" />}
            {analyticsTab === 'estado' && <LeadCharts leads={results} only="regiao" />}
            {analyticsTab === 'contatos' && <LeadCharts leads={results} only="contatos" />}
            {analyticsTab === 'vendas' && (
              <div>
                <div className="analytics-period-selector">
                  {PERIOD_OPTIONS.map((p) => (
                    <button
                      key={p.days}
                      className={`analytics-tab ${salesPeriod === p.days ? 'analytics-tab--active' : ''}`}
                      onClick={() => setSalesPeriod(p.days)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <LeadCharts leads={results} only="vendas" salesTimeline={salesTimeline} />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={excludedOpen} onClose={() => setExcludedOpen(false)}>
        <div style={{ color: 'var(--text-primary)' }}>
          <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserX size={20} color="#ff4d4d" /> Leads Excluídos (Sem Contato)
          </h2>
          <p style={{ opacity: 0.8, marginBottom: '1rem', fontSize: '0.9rem' }}>
            Estes leads foram encontrados, porém não possuem nem e-mail e nem telefone. Eles foram marcados como excluídos e não estão disponíveis para venda ou listagem de clientes.
          </p>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Fonte</th>
                </tr>
              </thead>
              <tbody>
                {excludedResults.length > 0 ? (
                  excludedResults.map((lead, idx) => (
                    <tr key={`excl-${idx}`}>
                      <td style={{ fontWeight: 600 }}>{lead.Nome || 'N/A'}</td>
                      <td>{lead.Categoria || 'N/A'}</td>
                      <td>{lead.Fonte || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', opacity: 0.5, padding: '2rem' }}>
                      Nenhum lead excluído.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </>
  );
}
